import { useState, useMemo, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, addDays, subDays } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar, RefreshCw, Clock, Trophy, Filter, ArrowLeft, Lock, Tv } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useScheduleForDate, formatDateDisplay, type ScheduledGame } from "@/hooks/useSchedule";
import { useWorldsScrape } from "@/hooks/useWorldsLive";
import { enrichWorldsGames } from "@/lib/worldsScheduleAdapter";
import GameDetailsModal from "@/components/GameDetailsModal";
import { useGameNotifications } from "@/hooks/useGameNotifications";
import { cn } from "@/lib/utils";
import { AuthContext } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";

type League = "NHL" | "AHL" | "ECHL" | "KHL" | "SHL" | "Allsvenskan" | "Liiga" | "Extraliga" | "DEL" | "NL" | "PWHL" | "SDHL" | "Naisten Liiga" | "Worlds";

const ALL_LEAGUES: League[] = ["Worlds", "NHL", "AHL", "ECHL", "KHL", "SHL", "Allsvenskan", "Liiga", "Extraliga", "DEL", "NL", "PWHL", "SDHL", "Naisten Liiga"];

const Schedule = () => {
  // Always scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLeagues, setSelectedLeagues] = useState<League[]>([]);
  const [selectedGame, setSelectedGame] = useState<ScheduledGame | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const dateStr = format(selectedDate, "yyyy-MM-dd");
  
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  
  const { data: rawGames, isLoading, error, refetch, isFetching } = useScheduleForDate(dateStr);
  const { data: worldsScrape } = useWorldsScrape();
  const games = useMemo(
    () => (rawGames ? enrichWorldsGames(rawGames, worldsScrape?.games) : rawGames),
    [rawGames, worldsScrape],
  );

  const dates = Array.from({ length: 7 }, (_, i) => addDays(new Date(), i));

  const navigateDate = (direction: "prev" | "next") => {
    setSelectedDate(prev => direction === "prev" ? subDays(prev, 1) : addDays(prev, 1));
  };

  const toggleLeague = (league: League) => {
    setSelectedLeagues(prev => 
      prev.includes(league) 
        ? prev.filter(l => l !== league)
        : [...prev, league]
    );
  };

  const clearFilters = () => setSelectedLeagues([]);
  const selectAllLeagues = () => setSelectedLeagues([...ALL_LEAGUES]);

  const filteredGames = useMemo(() => {
    if (!games) return [];
    if (selectedLeagues.length === 0) return games;
    return games.filter(game => selectedLeagues.includes(game.league as League));
  }, [games, selectedLeagues]);

  const gamesByLeague = filteredGames.reduce((acc, game) => {
    if (!acc[game.league]) acc[game.league] = [];
    acc[game.league].push(game);
    return acc;
  }, {} as Record<string, ScheduledGame[]>);

  const availableLeagues = useMemo(() => {
    if (!games) return [];
    return [...new Set(games.map(g => g.league))] as League[];
  }, [games]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="mb-4 gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>

        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Calendar className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Schedule & Watch</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Upcoming Fixtures & How to Watch</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* League Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Filter className="h-4 w-4" />
                  {selectedLeagues.length === 0 
                    ? "All Leagues" 
                    : `${selectedLeagues.length} League${selectedLeagues.length !== 1 ? "s" : ""}`}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-popover border-border z-50">
                <DropdownMenuLabel>Filter by League</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="flex gap-1 px-2 py-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="flex-1 h-7 text-xs"
                    onClick={selectAllLeagues}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="flex-1 h-7 text-xs"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </div>
                <DropdownMenuSeparator />
                {ALL_LEAGUES.map((league) => {
                  const hasGames = availableLeagues.includes(league);
                  return (
                    <DropdownMenuCheckboxItem
                      key={league}
                      checked={selectedLeagues.includes(league)}
                      onCheckedChange={() => toggleLeague(league)}
                      disabled={!hasGames && selectedLeagues.length > 0}
                      className={cn(!hasGames && "opacity-50")}
                    >
                      {league}
                      {hasGames && games && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          {games.filter(g => g.league === league).length}
                        </span>
                      )}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-2"
            >
              <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Active Filters */}
        {selectedLeagues.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-sm text-muted-foreground">Showing:</span>
            {selectedLeagues.map((league) => (
              <Badge 
                key={league} 
                variant="secondary" 
                className="cursor-pointer hover:bg-secondary/80"
                onClick={() => toggleLeague(league)}
              >
                {league}
                <span className="ml-1 text-muted-foreground">×</span>
              </Badge>
            ))}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 text-xs text-muted-foreground"
              onClick={clearFilters}
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Date Navigation */}
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate("prev")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex gap-2 overflow-x-auto pb-2 flex-1">
            {dates.map((date) => {
              const isSelected = format(date, "yyyy-MM-dd") === dateStr;
              return (
                <Button
                  key={date.toISOString()}
                  variant={isSelected ? "default" : "outline"}
                  className={cn(
                    "min-w-[100px] flex-col h-auto py-2",
                    isSelected && "bg-primary text-primary-foreground"
                  )}
                  onClick={() => setSelectedDate(date)}
                >
                  <span className="text-xs opacity-80">
                    {format(date, "EEE")}
                  </span>
                  <span className="font-semibold">
                    {format(date, "MMM d")}
                  </span>
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate("next")}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Current Date Display */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {formatDateDisplay(dateStr)}
          </h2>
          <p className="text-sm text-muted-foreground">
            {format(selectedDate, "EEEE, MMMM d, yyyy")}
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-card">
                <CardContent className="p-4">
                  <Skeleton className="h-6 w-32 mb-4" />
                  <div className="space-y-3">
                    {[1, 2].map((j) => (
                      <Skeleton key={j} className="h-16 w-full" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card className="bg-destructive/10 border-destructive">
            <CardContent className="p-6 text-center">
              <p className="text-destructive">Failed to load schedule. Please try again.</p>
              <Button variant="outline" onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Auth Required Message */}
        {!isLoading && !error && !user && filteredGames.length === 0 && (
          <Card className="bg-muted/50 border-primary/20">
            <CardContent className="p-12 text-center">
              <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
              <p className="text-foreground text-lg font-semibold">
                Sign in to access game schedules
              </p>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                Create a free account or sign in to view live games, schedules, and real-time scores across all major hockey leagues.
              </p>
              <Button 
                onClick={() => setShowAuthModal(true)} 
                className="mt-6"
              >
                Sign In / Register
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No Games (for authenticated users) */}
        {!isLoading && !error && user && filteredGames.length === 0 && (
          <Card className="bg-muted/50">
            <CardContent className="p-12 text-center">
              <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">
                {selectedLeagues.length > 0 
                  ? "No games found for selected leagues"
                  : "No games scheduled for this date"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedLeagues.length > 0 
                  ? "Try selecting different leagues or another date"
                  : "Try selecting a different date or check back later"}
              </p>
              {selectedLeagues.length > 0 && (
                <Button variant="outline" onClick={clearFilters} className="mt-4">
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Games by League */}
        {!isLoading && !error && Object.keys(gamesByLeague).length > 0 && (
          <div className="space-y-6">
            {Object.entries(gamesByLeague).map(([league, leagueGames]) => (
              <Card key={league} className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge
                      variant={league === "Worlds" ? "default" : "secondary"}
                      className={cn(
                        "text-sm font-semibold",
                        league === "Worlds" && "bg-primary text-primary-foreground"
                      )}
                    >
                      {league === "Worlds" ? "🏆 IIHF Worlds 2026" : league}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {leagueGames.length} game{leagueGames.length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {leagueGames.map((game) => (
                      <GameCard key={game.id} game={game} onClick={() => setSelectedGame(game)} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Game Details Modal */}
        <GameDetailsModal 
          game={selectedGame} 
          isOpen={!!selectedGame} 
          onClose={() => setSelectedGame(null)} 
        />

        {/* Auth Modal */}
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </main>
      <Footer />
    </div>
  );
};

interface GameCardProps {
  game: ScheduledGame;
  onClick: () => void;
}

const GameCard = ({ game, onClick }: GameCardProps) => {
  const isUpcoming = !game.isLive && !game.isFinished;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, " ");
  
  return (
    <div 
      className="flex flex-col p-4 rounded-lg bg-muted/30 border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer"
      onClick={onClick}
    >
      {/* Main row: teams + score/time */}
      <div className="flex items-center justify-between">
        {/* Home Team (displayed first/left) */}
        <div className="flex items-center gap-3 flex-1">
          {game.homeLogo ? (
            <img
              src={game.homeLogo}
              alt={game.homeTeam}
              className="w-10 h-10 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center text-xs font-bold">
              {game.homeAbbr}
            </div>
          )}
          <div>
            <p className="font-semibold text-foreground">{game.homeTeam}</p>
            <p className="text-xs text-muted-foreground">{game.homeAbbr}</p>
          </div>
        </div>

        {/* Score / Time */}
        <div className="flex flex-col items-center px-4 min-w-[120px]">
          {game.isLive ? (
            <>
              <Badge variant="destructive" className="animate-pulse mb-1">
                LIVE
              </Badge>
              <div className="flex items-center gap-2 text-xl font-bold">
                <span>{game.homeScore ?? 0}</span>
                <span className="text-muted-foreground">-</span>
                <span>{game.awayScore ?? 0}</span>
              </div>
              <span className="text-xs text-muted-foreground">{game.statusShort}</span>
            </>
          ) : game.isFinished ? (
            <>
              <Badge variant="secondary" className="mb-1">FINAL</Badge>
              <div className="flex items-center gap-2 text-xl font-bold">
                <span>{game.homeScore ?? 0}</span>
                <span className="text-muted-foreground">-</span>
                <span>{game.awayScore ?? 0}</span>
              </div>
              {game.statusShort !== "FT" && (
                <span className="text-xs text-muted-foreground">{game.statusShort}</span>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-primary mb-0.5">
                Puck Drop
              </span>
              <div className="flex items-center gap-1.5 text-foreground">
                <Clock className="h-3.5 w-3.5 text-primary" />
                <span className="text-base font-bold">{game.time}</span>
              </div>
              <span className="text-[10px] text-muted-foreground mt-0.5">
                {userTimezone}
              </span>
            </div>
          )}
        </div>

        {/* Away Team (displayed second/right) */}
        <div className="flex items-center gap-3 flex-1 justify-end text-right">
          <div>
            <p className="font-semibold text-foreground">{game.awayTeam}</p>
            <p className="text-xs text-muted-foreground">{game.awayAbbr}</p>
          </div>
          {game.awayLogo ? (
            <img
              src={game.awayLogo}
              alt={game.awayTeam}
              className="w-10 h-10 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center text-xs font-bold">
              {game.awayAbbr}
            </div>
          )}
        </div>
      </div>

      {/* Broadcast + puck drop info row — shown for all games with TV data */}
      {game.tvChannels && game.tvChannels.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border/30 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Tv className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium">Watch on:</span>
            </div>
            {game.tvChannels.map((channel) => (
              <Badge key={channel} variant="outline" className="text-xs px-2 py-0.5 bg-primary/5 border-primary/20 text-foreground">
                {channel}
              </Badge>
            ))}
          </div>
          {isUpcoming && (
            <span className="text-[10px] text-muted-foreground">
              {game.time} {userTimezone}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default Schedule;
